import React, { useCallback, useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useParams } from "react-router";

import { ApplicationEndpoints } from "@generated/application";
import applicationApi from "src/domains/application/module/api/applicationApi";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

import type { ExistingSystem, DetectedService } from "../StackEditPage/types";

const DEFAULT_RESOURCES = { cpu: "500m", memory: "512Mi" };

function authHeader() {
  return { Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}` };
}

export default function ReviewServicesPage(): React.ReactElement {
  const { orgAlias, projectAlias, sysAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
    sysAlias: string;
  }>();
  const nav = useAppNavigation();

  const [system, setSystem] = useState<ExistingSystem | null>(null);
  const [services, setServices] = useState<DetectedService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sysAlias) return;
    const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION.replace(":id", sysAlias)}`;
    axios
      .get<ExistingSystem>(url, { headers: authHeader() })
      .then((r) => {
        setSystem(r.data);
        const stackConfig = (r.data.stackConfig ?? {}) as Record<
          string,
          unknown
        >;
        const detected = Array.isArray(stackConfig._detectedServices)
          ? (stackConfig._detectedServices as DetectedService[])
          : [];
        setServices(
          detected.map((s) => ({
            ...s,
            resources: s.resources ?? { ...DEFAULT_RESOURCES },
          })),
        );
        setLoading(false);
      })
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err)
          ? ((err.response?.data as { message?: string })?.message ??
            err.message)
          : "Failed to load stack.";
        setError(msg);
        setLoading(false);
      });
  }, [sysAlias]);

  const updateService = useCallback(
    (index: number, patch: Partial<DetectedService>) => {
      setServices((prev) =>
        prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
      );
    },
    [],
  );

  const removeService = useCallback((index: number) => {
    setServices((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    if (!sysAlias || !orgAlias || !projectAlias) return;
    setSaving(true);
    setSaveError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", sysAlias)}`;
      await axios.patch(
        url,
        {
          stackConfig: {
            ...(system?.stackConfig ?? {}),
            _detectedServices: services,
          },
        },
        { headers: authHeader() },
      );
      await applicationApi.buildAndDeploy(sysAlias);
      nav.goToStackDetails(orgAlias, projectAlias, sysAlias);
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? ((err.response?.data as { message?: string })?.message ?? err.message)
        : "Failed to save services.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [nav, orgAlias, projectAlias, services, sysAlias, system?.stackConfig]);

  const handleCopyRushaYml = useCallback(() => {
    const yml = `# Auto-generated rusha.yml review page\nservices:\n${services
      .map(
        (s) =>
          `  - name: ${s.name}\n    type: ${s.type}\n    port: ${s.port ?? 8080}\n    internetFacing: ${s.internetFacing ?? true}\n`,
      )
      .join("")}`;
    void navigator.clipboard.writeText(yml);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [services]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <Typography color="text.secondary">
          Loading detected services…
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => window.history.back()}>
          Go back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() =>
          nav.goToStackDetails(orgAlias!, projectAlias!, sysAlias!)
        }
        sx={{ mb: 2, textTransform: "none" }}
      >
        Back to stack
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Review Detected Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {system?.name ?? sysAlias} — confirm or tweak the services Rusha
            found before deploying.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Copy rusha.yml snippet">
            <IconButton onClick={handleCopyRushaYml}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={
              saving ? null : <RocketLaunchIcon sx={{ fontSize: 18 }} />
            }
            onClick={handleSave}
            disabled={saving || services.length === 0}
          >
            {saving ? "Saving…" : "Confirm & Deploy"}
          </Button>
        </Stack>
      </Stack>

      {copied && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Copied services summary to clipboard
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Port</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  Dockerfile / Build
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>CPU</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Memory</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Internet-facing</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((svc, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={svc.name}
                      onChange={(e) =>
                        updateService(idx, { name: e.target.value })
                      }
                      sx={{ minWidth: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={svc.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={svc.port ?? ""}
                      placeholder="8080"
                      onChange={(e) =>
                        updateService(idx, {
                          port: e.target.value
                            ? parseInt(e.target.value, 10)
                            : undefined,
                        })
                      }
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    {svc.dockerfilePath ?? svc.buildCommand ?? "—"}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={svc.resources?.cpu ?? DEFAULT_RESOURCES.cpu}
                      onChange={(e) =>
                        updateService(idx, {
                          resources: {
                            ...(svc.resources ?? DEFAULT_RESOURCES),
                            cpu: e.target.value,
                          },
                        })
                      }
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={svc.resources?.memory ?? DEFAULT_RESOURCES.memory}
                      onChange={(e) =>
                        updateService(idx, {
                          resources: {
                            ...(svc.resources ?? DEFAULT_RESOURCES),
                            memory: e.target.value,
                          },
                        })
                      }
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={svc.internetFacing ? "Yes" : "No"}
                      color={svc.internetFacing ? "success" : "default"}
                      size="small"
                      onClick={() =>
                        updateService(idx, {
                          internetFacing: !(svc.internetFacing ?? true),
                        })
                      }
                      sx={{ cursor: "pointer" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit is inline — use the fields above">
                      <IconButton size="small" disabled>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove service">
                      <IconButton
                        size="small"
                        onClick={() => removeService(idx)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No services detected. Go back and re-run the repo
                      inspector.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
        Need a database, cache, or queue? Use the <strong>Resources</strong> tab
        on the stack page to provision them independently and attach them to
        this stack.
      </Alert>

      <Stack direction="row" justifyContent="space-between">
        <Button
          variant="outlined"
          onClick={() =>
            nav.goToStackDetails(orgAlias!, projectAlias!, sysAlias!)
          }
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || services.length === 0}
        >
          {saving ? "Saving…" : "Confirm & Deploy"}
        </Button>
      </Stack>
    </Container>
  );
}
